<template>
  <div class="settings-content">
    <div class="name-field-container">
      <label>Organization Name</label>

      <input
        class="form-control"
        v-model="changedOrganisationName"
        type="text"
        name="OrganisationName"
        id="OrganisationName"
        required
        :disabled="!isAdmin"
      />
    </div>

    <div class="second-row">
      <ApperanceScheme :organisation="organisation" />
    </div>

    <div class="bottom-row">
      <div class="created-details-container">
        <div class="created-details-row">
          <p class="created-details">
            <span class="details-heading">Created on: </span>
            <span class="details-values">{{ formattedDate }}</span>
          </p>
        </div>

        <div class="created-details-row">
          <p class="created-details">
            <span class="details-heading">Created by: </span>
            <span class="details-values">{{ organisation?.created_by || 'N/A' }}</span>
          </p>
        </div>
      </div>

      <div class="button-container">
        <BaseButton
          :disabled="isCancelButtonDisabled"
          theme="secondary"
          @click="handleCancelNamechange"
        >
          Cancel
        </BaseButton>

        <BaseButton
          :disabled="isUpdateButtonDisabled"
          :loading="isSaving"
          icon="save"
          @click="handleUpdateOrganisation"
        >
          Update
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
  import BaseButton from '@/components/common/Button.vue';
  import ApperanceScheme from '@/components/admin/OrganisationDetails/Settings/ApperanceScheme.vue';
  import Input from '@/components/common/Input.vue';
  import { adminApiPostOrganisation } from '@/api/organisation.js';
  import { useStore } from '@/store';
  import moment from 'moment-timezone';
  import { GENERIC_ERROR_MESSAGE } from '@/constant/const';

  export default {
    name: 'Settings',

    components: {
      BaseButton,
      Input,
      ApperanceScheme,
    },

    props: {
      organisation: Object,
    },

    data() {
      return {
        changedOrganisationName: this.organisation?.name ?? '',
        formattedDate: this.organisation?.created
          ? moment(this.organisation.created).format('YYYY-MM-DD [at] HH:mm:ss')
          : null,
        isSaving: false,
      };
    },
    computed: {
      isCancelButtonDisabled() {
        return this.changedOrganisationName === this.organisation?.name;
      },
      isUpdateButtonDisabled() {
        return (
          this.changedOrganisationName === this.organisation?.name ||
          this.changedOrganisationName.length <= 3 ||
          this.isSaving
        );
      },
      isAdmin() {
        return this.$store.getters.isAdministrator;
      },
    },
    setup() {
      const store = useStore();

      async function updateOrganisation(updatedOrganisation) {
        const response = await adminApiPostOrganisation(
          this.organisation?.organisation_id,
          updatedOrganisation,
        );
      }

      return {
        store,
        updateOrganisation,
      };
    },
    watch: {
      organisation(newOrg) {
        this.changedOrganisationName = newOrg?.name ?? '';
      },
    },
    methods: {
      async handleUpdateOrganisation() {
        this.isSaving = true;
        try {
          const data = {
            orgId: this.organisation.organisation_id,
            orgData: {
              name: this.changedOrganisationName,
            },
          };
          const [successful, error] = await this.$store.dispatch('ADMIN_ORGANISATION_UPDATE', data);
          if (successful) {
            this.$toasted.global.general_success({
              message:
                this.$lang?.organizations?.organization_updated ??
                'Organization updated successfully',
            });
            this.$emit('organisationUpdated', this.changedOrganisationName);
          } else {
            this.$toasted.global.general_error({
              message: error?.response?.data?.message ?? GENERIC_ERROR_MESSAGE,
            });
          }
        } catch (error) {
          this.$toasted.global.general_error({
            message: error?.response?.data?.message ?? GENERIC_ERROR_MESSAGE,
          });
        }
        this.isSaving = false;
      },
      handleCancelNamechange() {
        this.changedOrganisationName = this.organisation?.name ?? '';
      },
    },
  };
</script>

<style scoped lang="scss">
  #OrganisationName {
    color: $primaryText;
  }

  .label {
    display: block;
    font-size: 14px;
  }
  .settings-content {
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 1fr 1fr;
    height: 100%;
    box-sizing: border-box;
    padding-top: 15px;
    position: relative;
  }
  input {
    width: 100%;
    font-size: 16px;
    border: solid 0.5px;
    border-color: #f0f0f0;
    border-radius: 5px;
    padding-left: 10px;
    padding-right: 10px;
  }
  .name-field-container,
  .timezone-field-container {
    grid-column: span 1;
    padding-bottom: 20px;
    display: flex;
    gap: 8px;
    flex-direction: column;
  }

  .second-row {
    grid-column: span 2;
    overflow-y: auto;
  }

  .bottom-row {
    grid-column: span 2;
    display: grid;
    grid-template-columns: 1fr 1fr;
    position: sticky;
    bottom: -20px;
    z-index: 1000;
    /* background-color: #f7f8f7; */
    padding-top: 20px;
    padding-bottom: 0px;
    border-top: solid 2px #f0f0f0;
  }
  .created-details-container {
    align-items: flex-end;
    display: flex;
  }
  .created-details-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
  }

  .created-details {
    margin: 0;
    font-family: Poppins;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0%;
  }

  .details-heading {
    color: #6a6b6b;
  }

  .details-values {
    color: #333332;
    font-family: Poppins;
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0%;
  }

  .button-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 15px;
    max-height: 50px;
  }

  .ui-button {
    padding: 8px 16px;
  }
</style>
